import Icon, { IconSizes } from '@/components/ui/Icon';
import classnames from 'classnames';

enum ButtonColor {
  Black = 'black',
  White = 'white',
  Gray = 'gray',
  GreyDarker = 'gray-darker',
  OutlineBlack = 'outline-black',
  Red = 'red',
  Gradient = 'gradient'
}

enum ButtonSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export type RoundedButtonProps = {
  icon: string;
  color?: `${ButtonColor}`;
  disabled?: boolean;
  buttonSize?: `${ButtonSize}`;
  label?: React.ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

const RoundedButton: React.FC<RoundedButtonProps> = ({
  icon,
  color = ButtonColor.Black,
  disabled,
  buttonSize = ButtonSize.Medium,
  label,
  className,
  ...props
}) => {
  const buttonClasses = classnames(
    'group relative flex items-center justify-center gap-1 rounded-full transition',
    {
      'aspect-square': !label,
      'px-2': !!label,
      'h-6': buttonSize === ButtonSize.Small,
      'h-8': buttonSize === ButtonSize.Medium,
      'h-10': buttonSize === ButtonSize.Large
    },
    {
      'bg-black': color === ButtonColor.Black,
      'hover:bg-gray-dark': color === ButtonColor.Black,
      'active:bg-gray-dark': color === ButtonColor.Black,
      'disabled:bg-gray': color === ButtonColor.Black
    },
    {
      'bg-white': color === ButtonColor.White,
      'hover:bg-gray-lightest': color === ButtonColor.White,
      'active:bg-gray-light': color === ButtonColor.White,
      'disabled:bg-gray-light': color === ButtonColor.White,
      'focus-visible:outline-white': color === ButtonColor.White
    },
    {
      'bg-gray-light': color === ButtonColor.Gray,
      'hover:bg-silver': color === ButtonColor.Gray,
      'active:bg-gray-light': color === ButtonColor.Gray,
      'disabled:bg-gray-light': color === ButtonColor.Gray
    },
    {
      'bg-gray-darker': color === ButtonColor.GreyDarker,
      'hover:bg-silver': color === ButtonColor.GreyDarker,
      'active:bg-gray-darker': color === ButtonColor.GreyDarker,
      'disabled:bg-gray-darker': color === ButtonColor.GreyDarker
    },
    {
      'border border-gray bg-white': color === ButtonColor.OutlineBlack,
      'hover:border-gray-dark': color === ButtonColor.OutlineBlack,
      'active:border-gray-dark': color === ButtonColor.OutlineBlack,
      'disabled:border-gray-light': color === ButtonColor.OutlineBlack
    },
    {
      'bg-red': color === ButtonColor.Red,
      'hover:bg-red-dark': color === ButtonColor.Red,
      'active:bg-red-dark': color === ButtonColor.Red,
      'disabled:bg-gray-light': color === ButtonColor.Red
    },
    {
      'before:absolute before:inset-0 before:-z-1 before:size-full before:rounded-[inherit] before:bg-gradient':
        color === ButtonColor.Gradient,
      'enabled:hover:before:opacity-80': color === ButtonColor.Gradient,
      'enabled:active:before:opacity-80': color === ButtonColor.Gradient,
      'disabled:before:bg-gray': color === ButtonColor.Gradient
    },
    className
  );

  const colorClasses = classnames(
    'opacity-100 transition',
    {
      'text-white': color === ButtonColor.Black,
      'group-disabled:opacity-70': color === ButtonColor.Black
    },
    {
      'text-black': color === ButtonColor.White,
      'group-disabled:opacity-30': color === ButtonColor.White
    },
    {
      'text-black': color === ButtonColor.Gray,
      'group-disabled:opacity-30': color === ButtonColor.Gray
    },
    {
      'text-white': color === ButtonColor.GreyDarker,
      'group-disabled:opacity-30': color === ButtonColor.GreyDarker
    },
    {
      'text-black': color === ButtonColor.OutlineBlack,
      'group-disabled:opacity-30': color === ButtonColor.OutlineBlack
    },
    {
      'text-white': color === ButtonColor.Red,
      'group-active:opacity-70': color === ButtonColor.Red,
      'group-disabled:text-white group-disabled:opacity-70': color === ButtonColor.Red
    },
    {
      'text-white': color === ButtonColor.Gradient,
      'group-active:opacity-70': color === ButtonColor.Gradient,
      'group-disabled:text-white group-disabled:opacity-70': color === ButtonColor.Gradient
    }
  );

  const iconCLasses = classnames(colorClasses);
  const labelClasses = classnames(colorClasses, 'text-1-fixed mr-1 font-bold');

  let iconSize: IconSizes = 'medium';
  switch (buttonSize) {
    case ButtonSize.Small:
      iconSize = 'x-small';
      break;
    case ButtonSize.Medium:
      iconSize = 'small';
      break;
    case ButtonSize.Large:
      iconSize = 'large';
      break;
    default:
      break;
  }

  return (
    <button data-testid="rounded-button" disabled={disabled} className={buttonClasses} {...props}>
      <span>
        <Icon className={iconCLasses} name={icon} size={iconSize} />
      </span>
      {label && <span className={labelClasses}>{label}</span>}
    </button>
  );
};

export default RoundedButton;
